import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

// Canonical Wolf Tech app store: persists nightMode + selectedDeckId across
// reloads. Deliberately narrow — server data lives in React Query / Dexie,
// auth lives in AuthContext (it owns the Hub listener side-effect).
//
// Also holds profile preferences (display name, avatar, daily goal) and the
// unlocked-badge map. These are device-local for v1 — re-installs reset them.
const useAppStore = create(
  persist(
    (set) => ({
      nightMode: true,
      selectedDeckId: null,
      displayName: '',
      avatarEmoji: '🎓',
      dailyGoal: 20,
      unlockedBadges: {}, // { [badgeId]: ISO timestamp }
      setNightMode: (value) => set({ nightMode: value }),
      toggleNightMode: () => set((s) => ({ nightMode: !s.nightMode })),
      setSelectedDeckId: (id) => set({ selectedDeckId: id }),
      setDisplayName: (name) => set({ displayName: name }),
      setAvatarEmoji: (emoji) => set({ avatarEmoji: emoji }),
      setDailyGoal: (n) => set({ dailyGoal: Math.max(1, Math.min(500, Number(n) || 0)) }),
      unlockBadges: (ids) => set((s) => {
        if (!ids || ids.length === 0) return {};
        const now = new Date().toISOString();
        const next = { ...s.unlockedBadges };
        for (const id of ids) {
          if (!next[id]) next[id] = now;
        }
        return { unlockedBadges: next };
      }),
      // Reset per-user state on sign-out so a shared browser doesn't leak
      // streaks / badges / display name to the next signed-in user.
      // Theme (nightMode) is a device preference and stays.
      resetUserState: () => set({
        selectedDeckId: null,
        displayName: '',
        avatarEmoji: '🎓',
        dailyGoal: 20,
        unlockedBadges: {},
      }),
    }),
    {
      name: 'recall-app',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        nightMode: state.nightMode,
        selectedDeckId: state.selectedDeckId,
        displayName: state.displayName,
        avatarEmoji: state.avatarEmoji,
        dailyGoal: state.dailyGoal,
        unlockedBadges: state.unlockedBadges,
      }),
    }
  )
);

export default useAppStore;
